'use client';

import { Box, Button, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { Cancel } from "@mui/icons-material";
import { Direction } from "@/app/features/sites/events/types/types";

export default function EventListHeader() {
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const { replace, push } = useRouter();
    const [searchTerm, setSearchTerm] = useState('');

    const eventId = searchParams.get("id");
    const direction = searchParams.get("direction") || 'forward';

    // Get the initial search term from URL if it exists
    useEffect(() => {
        const initialQuery = searchParams.get('query') || '';
        setSearchTerm(initialQuery);
    }, [searchParams]);

    useEffect(() => {
        const currentQuery = searchParams.get('query') || '';
        if (currentQuery === (searchTerm || '')) return;

        const debounceTimeout = setTimeout(() => {
            const params = new URLSearchParams(searchParams);

            // Reset pagination when the query changes
            params.delete('page');

            if (searchTerm) {
                params.set('query', searchTerm);
            } else {
                params.delete('query');
            }
            replace(`${pathname}?${params.toString()}`);
        }, 250);

        // Cleanup timeout on each searchTerm change
        return () => clearTimeout(debounceTimeout);
    }, [searchTerm, searchParams, pathname, replace]);

    function updateDirection(direction: Direction) {
        const params = new URLSearchParams(searchParams);
        params.delete('direction');
        params.set('direction', direction);
        push(`${pathname}?${params.toString()}`);
    };

    function onShowAllEvents() {
        const params = new URLSearchParams(searchParams);
        params.delete('id');
        push(`${pathname}?${params.toString()}`);
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flex: '1 1 100%',
                maxWidth: '100vw',
                minWidth: '100vw',
                justifyContent: 'center',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                padding: (theme) => ({
                    xs: 2,
                    md: theme.spacing(4, '10%'),
                }),
            }}
        >
            <Typography component="h1" variant="h1" textAlign="center">
                Veranstaltungen
            </Typography>
            {eventId
                ? (
                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        onClick={onShowAllEvents}
                    >
                        Alle Veranstaltungen anzeigen
                    </Button>
                )
                : (
                    <Stack spacing={2} sx={{ width: '100%' }} alignItems="center">

                        <TextField
                            placeholder="Veranstaltung suchen ..."
                            onChange={(e) => setSearchTerm(e.target.value)}
                            value={searchTerm}
                            fullWidth
                            slotProps={{
                                htmlInput: {
                                    'aria-label': 'Veranstaltung suchen',
                                },
                                input: {
                                    endAdornment: searchTerm !== '' && (
                                        <IconButton aria-label="Clear search" onClick={() => setSearchTerm('')} tabIndex={-1}>
                                            <Cancel/>
                                        </IconButton>
                                    )
                                }
                            }}
                        />
                        <ToggleButtonGroup
                            value={direction}
                            color="primary"
                            onChange={(_e, value) =>
                                updateDirection(value)
                            }
                            exclusive
                            fullWidth
                        >
                            <ToggleButton value="backward">Vergangene Veranstaltungen</ToggleButton>
                            <ToggleButton value="forward">Kommende Veranstaltungen</ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                )
            }
        </Box>
    )
}
